import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import {
  type Opportunity,
  OpportunityApi,
  OpportunityFromJSON,
  type RecurringPlan,
  type Stage,
  type UpdateOpportunityDto,
} from '@/api-swagger'
import type { CreateOpportunityDto } from '@/api-swagger/models/CreateOpportunityDto'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useUserStore } from '@/stores/useUserStore'
import { useMyTeamStore } from '@/stores/useMyTeamStore'
import { useSelectionsStore } from '@/stores/useSelectionsStore'
import { usePartnerStore } from '@/stores/usePartnerStore'
import { useSuiteStore } from '@/stores/useSuiteStore'
import type { Context } from '@/utils/types'
import { UserRole } from '@/utils/enums'
import { withApiState } from '@/utils/withApiState'

export const useOpportunityStore = defineStore('opportunity', () => {
  const opportunity = ref<Opportunity | null>(null)
  const selectedOpportunity = ref<Opportunity | null>(null)
  const opportunities = ref<Opportunity[] | null>(null)
  const partnerOpportunities = ref<Opportunity[] | null>(null)
  const partnerIsActiveOpportunities = ref<boolean | null>(null)
  const partnerActiveOpportunities = ref<Opportunity[] | null>(null)
  const searchOpportunities = ref<Opportunity[] | null>(null)
  const loadingOpportunities = ref(false)
  const stages = ref<Stage[] | null>(null)
  const recurringPlans = ref<RecurringPlan[] | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const api = new OpportunityApi()

  const userStore = useUserStore()
  const myTeamStore = useMyTeamStore()
  const selectionsStore = useSelectionsStore()
  const partnerStore = usePartnerStore()
  const suiteStore = useSuiteStore()

  const userConnected = computed(() => userStore.userConnected)
  const role = computed(() => userStore.role)
  const salesFamily = computed(() => selectionsStore.salesFamilyOpportunitySelections)

  const filters = ref<{
    selectedCompany: string
    selectedCommercials: string[]
    selectedPriority: string
    isCheckedDateDeadLine: boolean
    selectedDate: { year: number; month: number } | null
    yearDeadLine: { year: number } | null
    companies: { id: string; value: string; label: string }[]
  }>({
    selectedCompany: '',
    selectedCommercials: [] as string[],
    selectedPriority: '',
    isCheckedDateDeadLine: false,
    selectedDate: {
      year: new Date().getFullYear(),
      month: new Date().getMonth(),
    },
    yearDeadLine: {
      year: new Date().getFullYear(),
    },
    companies: [
      {
        id: 'all',
        value: '',
        label: 'Tous',
      },
    ],
  })

  const loadOpportunities = async () => {
    await withApiState(
      async () => {
        if (!userConnected.value) {
          const boostCoreStore = useBoostCorePermissionStore()
          const email = boostCoreStore.user?.email
          if (email) await userStore.getUserByEmail(email)
        }

        if (userConnected.value) {
          const date = filters.value.selectedDate
          const formattedDate =
            date && date.year && date.month !== undefined
              ? `${date.year}-${String(date.month + 1).padStart(2, '0')}`
              : ''

          let ids = [userConnected.value.id]
          const email = userConnected.value.email

          if (role.value === UserRole.Manager) {
            // Source unique : useMyTeamStore (boostcore) — odooIds inclut le manager
            ids = myTeamStore.odooIds.length > 0 ? [...myTeamStore.odooIds] : ids
          }

          if (role.value === UserRole.Manager) {
            await getByMyCommercialsId(ids, formattedDate, filters.value.isCheckedDateDeadLine)
          } else {
            await getByAuthorEmail(email!, formattedDate, filters.value.isCheckedDateDeadLine)
          }
        }
      },
      {
        loading: loadingOpportunities,
        error,
        errorText: 'Erreur lors du chargement des opportunités',
        logLabel: 'Erreur lors du chargement des opportunités:',
      },
    )
  }

  const getOpportunityById = async (id: number) => {
    return withApiState(
      async () => {
        const response = await api.getOpportunityById({ id })
        if (response) {
          return {
            ...response,
            userName: userStore.getLocalInfoUserById(response.userId) || '',
          }
        } else {
          throw new Error('Affaire non trouvée.')
        }
      },
      { loading, error, logLabel: 'Erreur API :', rethrow: true },
    )
  }

  const getOpportunitiesByParentId = async (id: number) => {
    loadingOpportunities.value = true
    partnerIsActiveOpportunities.value = null
    try {
      const suiteResponse = await suiteStore.suiteFetch(`opportunity/${id}/opportunities`, { method: 'GET' })
      let data: Opportunity[]
      if (suiteResponse && suiteResponse.ok) {
        const raw = await suiteResponse.json()
        data = Array.isArray(raw)
          ? raw.map((row: Record<string, unknown>) => OpportunityFromJSON(row))
          : []
      } else {
        data = await api.getOpportunitiesByListParentId({ ids: [id.toString()] })
      }
      partnerOpportunities.value = data.map((opp) => ({
        ...opp,
        userName: userStore.getLocalInfoUserById(opp.userId)?.name || '',
      }))
      checkPartnerActiveOpportunityByOtherUser(id)
    } catch (err) {
      console.error('Erreur API :', err)
      partnerOpportunities.value = []
    } finally {
      loadingOpportunities.value = false
    }
  }

  const checkPartnerActiveOpportunityByOtherUser = async (partnerId: number) => {
    error.value = null
    partnerIsActiveOpportunities.value = null

    try {
      const response = await api.checkPartnerActiveOpportunityByOtherUser({
        partnerId,
      })

      if (response) {
        partnerIsActiveOpportunities.value = response.hasActiveOpportunity!
        return response
      }
    } catch (err) {
      console.error('Erreur API :', err)
      throw err
    }
  }

  const getActiveOpportunitiesByParentId = async (id: number) => {
    await withApiState(
      async () => {
        const response = await api.getActiveOpportunitiesByParentIds({ ids: [id.toString()] })
        if (response) {
          partnerActiveOpportunities.value = response.map((opp) => ({
            ...opp,
            userName: userStore.getLocalInfoUserById(opp.userId)?.name || '',
          }))
        } else {
          throw new Error('Affaires non trouvées.')
        }
      },
      { loading: loadingOpportunities, logLabel: 'Erreur API :' },
    )
  }

  const updateOpportunity = async (id: number, data: UpdateOpportunityDto, context: Context) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.updateOpportunity({ id: id, updateOpportunityDto: data })

      if (response) {
        const updatedOpportunity = await getOpportunityById(id)
        if (opportunity.value && opportunity.value.id === id) {
          opportunity.value = {
            ...opportunity.value,
            ...updatedOpportunity,
            userName: userStore.getLocalInfoUserById(opportunity.value.userId)?.name || '',
          }
        }

        const updateList = (list: Opportunity[]) => {
          if (!list) return
          const index = list.findIndex((o) => o.id === id)
          if (index !== -1) {
            list[index] = <Opportunity>{ ...list[index], ...updatedOpportunity }
          }
        }

        switch (context) {
          case 'user':
            await loadOpportunities()
            break
          case 'partner':
            updateList(partnerOpportunities.value ?? [])
            break
          case 'search':
            updateList(searchOpportunities.value ?? [])
            break
        }
      }
    } catch (err) {
      throw new Error("Erreur lors de la mise à jour de l'affaire.")
    } finally {
      loading.value = false
    }
  }

  const getByAuthorEmail = async (
    userEmail: string,
    date?: string,
    isCheckedDateDeadLine?: boolean,
  ) => {
    loading.value = true

    let params: { date?: string; isCheckedDateDeadLine?: boolean } = {}
    if (isCheckedDateDeadLine) params = { ...params, isCheckedDateDeadLine: isCheckedDateDeadLine }
    if (date && date.trim() !== '') params.date = date

    try {
      const response = (await api.getOpportunities(params)) || []
      if (response) {
        opportunities.value = response.map((opp) => ({
          ...opp,
          userName: userStore.getLocalInfoUserById(opp.userId)?.name || '',
        }))
      } else {
        throw new Error('Affaires non trouvées.')
      }
    } catch (error) {
      console.error(error)
    } finally {
      loading.value = false
    }
  }

  const getByMyCommercialsId = async (
    ids: number[],
    date?: string,
    isCheckedDateDeadLine?: boolean,
  ) => {
    loading.value = true

    let params: { date?: string; isCheckedDateDeadLine?: boolean } = {}
    if (date && date.trim() !== '') params.date = date
    if (isCheckedDateDeadLine) params = { ...params, isCheckedDateDeadLine: isCheckedDateDeadLine }

    try {
      const response = (await api.getOpportunities(params)) || []
      if (response) {
        opportunities.value = response.map((opp) => ({
          ...opp,
          userName: userStore.getLocalInfoUserById(opp.userId)?.name || '',
        }))
      } else {
        throw new Error('Affaires non trouvées.')
      }
    } catch (error) {
      console.error(error)
    } finally {
      loading.value = false
    }
  }

  const getAllStages = async () => {
    await withApiState(
      async () => {
        stages.value = await api.getAllStages()
      },
      { loading },
    )
  }

  const getAllRecurringPlans = async () => {
    if (recurringPlans.value && recurringPlans.value.length > 0) return
    try {
      recurringPlans.value = await api.getAllRecurringPlans()
    } catch (error) {
      console.error(error)
    }
  }

  const createOpportunity = async (data: CreateOpportunityDto, context: Context) => {
    loading.value = true
    error.value = null

    try {
      const response = await api.createOpportunity({ createOpportunityDto: data })
      if (response) {
        const createdOpportunity = await getOpportunityById(response.id)

        if (createdOpportunity.partnerId)
          await partnerStore.updateScoreByPartnerId(createdOpportunity.partnerId.id)

        const updateList = (list: Opportunity[]) => {
          if (!list) return
          list.push(<Opportunity>createdOpportunity)
        }

        switch (context) {
          case 'user':
            await loadOpportunities()
            break
          case 'partner':
            updateList(partnerOpportunities.value ?? [])
            break
          case 'search':
            updateList(searchOpportunities.value ?? [])
            break
        }
        return {
          ...createdOpportunity,
        }
      }
    } catch (err) {
      console.error('Erreur API :', err)
      throw new Error("Erreur API lors de la création de l'affaire.")
    } finally {
      loading.value = false
    }
  }

  const searchOpportunitiesByPartnerIds = async (partnerIds: string[]) => {
    await withApiState(
      async () => {
        if (!partnerIds || partnerIds.length === 0) {
          searchOpportunities.value = []
          return
        }

        const response = await api.getOpportunitiesByListParentId({ ids: partnerIds })
        if (!response) {
          throw new Error('Affaires non trouvées.')
        }

        if (response) {
          searchOpportunities.value = response.map((opp) => ({
            ...opp,
            userName: userStore.getLocalInfoUserById(opp.userId)?.name || '',
          }))
        }
      },
      { loading: loadingOpportunities, logLabel: 'Erreur API :' },
    )
  }

  // Tous les filtres qui nécessitent de refaire un appel
  watch(
    [
      () => filters.value.selectedDate,
      () => filters.value.selectedCommercials,
      () => role.value,
      () => filters.value.isCheckedDateDeadLine,
    ],
    async (
      [newDate, newCommercial, newRole, newIsCheckedDateDeadLine],
      [oldDate, oldCommercial, oldRole, oldIsCheckedDateDeadLine],
    ) => {
      const dateChanged =
        (!newDate?.year && oldDate?.year) ||
        (!newDate?.month && oldDate?.month) ||
        newDate?.year !== oldDate?.year ||
        newDate?.month !== oldDate?.month

      const commercialChanged = newCommercial !== oldCommercial
      const roleChanged = newRole !== oldRole
      const isCheckedDateDeadLineChanged = newIsCheckedDateDeadLine !== oldIsCheckedDateDeadLine

      // On vide les filtres seulement si on passe date signature dépassée à true
      if (isCheckedDateDeadLineChanged && newIsCheckedDateDeadLine === true) {
        filters.value.selectedCompany = ''
        filters.value.selectedPriority = ''
        filters.value.selectedDate = null
      }

      // Si le rôle change et devient "commercial", on vide le filtre des commerciaux
      if (roleChanged && newRole === 'commercial') {
        filters.value.selectedCommercials = []
      }

      if (dateChanged || commercialChanged || roleChanged || isCheckedDateDeadLineChanged) {
        await loadOpportunities()
      }
    },
    { deep: true },
  )

  // Tous les autres filtres
  const filteredOpportunities = computed(() => {
    filters.value.companies = [
      {
        id: 'all',
        value: '',
        label: 'Tous',
      },
      ...salesFamily.value.map((value) => {
        return {
          id: value.value,
          value: value.value,
          label: value.name,
        }
      }),
    ]

    if (!opportunities.value) return []

    return opportunities.value.filter((opp) => {
      const matchPortfolio =
        !filters.value.selectedCompany || opp.salesFamily === filters.value.selectedCompany

      const priorityNum = Number(opp.priority)
      let matchPriority = true
      if (filters.value.selectedPriority === 'true') {
        matchPriority = priorityNum >= 3
      } else if (filters.value.selectedPriority === 'false') {
        matchPriority = priorityNum < 3
      }

      let matchCommercial = true
      if (filters.value.selectedCommercials.length > 0) {
        const ownerEmail = userStore
          .getCommercialEmailByOdooUserId(opp.userId)
          .trim()
          .toLowerCase()
        matchCommercial =
          ownerEmail !== '' &&
          filters.value.selectedCommercials.some(
            (e) => e.trim().toLowerCase() === ownerEmail,
          )
      }

      return matchPortfolio && matchPriority && matchCommercial
    })
  })

  const setSelectedOpportunity = (opp: Opportunity | null) => {
    selectedOpportunity.value = opp
  }

  return {
    opportunity,
    selectedOpportunity,
    opportunities,
    partnerOpportunities,
    partnerActiveOpportunities,
    searchOpportunities,
    loadingOpportunities,
    filteredOpportunities,
    stages,
    recurringPlans,
    loading,
    error,
    filters,
    userConnected,
    role,
    partnerIsActiveOpportunities,
    loadOpportunities,
    getOpportunityById,
    getByMyCommercialsId,
    getAllStages,
    getAllRecurringPlans,
    getOpportunitiesByParentId,
    getActiveOpportunitiesByParentId,
    searchOpportunitiesByPartnerIds,
    updateOpportunity,
    getByAuthorEmail,
    createOpportunity,
    setSelectedOpportunity,
  }
})
